import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { MessageCircle, Send, X, Minimize2, Circle } from 'lucide-react';

const presenceColor = { available: '#22c55e', busy: '#eab308', offline: '#9ca3af' };

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [started, setStarted] = useState(false);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (open && !started) loadStaff();
  }, [open]);

  useEffect(() => {
    if (chatId) {
      pollRef.current = setInterval(async () => {
        try { const r = await api.get(`/teams/widget/messages/${chatId}`); setMessages(r.data); } catch {}
      }, 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [chatId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadStaff = async () => {
    try { const r = await api.get('/teams/presence'); setStaff(r.data.filter(s => s.presence !== 'offline' && !s.is_incognito)); } catch {}
  };

  const handleStart = async () => {
    if (!name.trim()) return;
    if (!selectedStaff && staff.length > 0) setSelectedStaff(staff[0]);
    const target = selectedStaff || staff[0];
    if (!target) return;
    setSending(true);
    try {
      const r = await api.post('/teams/widget/message', {
        name, email, message: `${name} started a chat`, staff_email: target.email, chat_id: null,
      });
      setChatId(r.data.chat_id);
      setStarted(true);
    } catch {}
    setSending(false);
  };

  const handleSend = async () => {
    if (!input.trim() || !chatId) return;
    setSending(true);
    try {
      await api.post('/teams/widget/message', {
        name, email, message: input, staff_email: selectedStaff?.email || '', chat_id: chatId,
      });
      setInput('');
      const r = await api.get(`/teams/widget/messages/${chatId}`);
      setMessages(r.data);
    } catch {}
    setSending(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+84px)] right-4 lg:bottom-6 lg:right-6 z-40 h-14 w-14 rounded-full bg-[#30ace2] text-white shadow-lg hover:bg-[#37c9ee] flex items-center justify-center transition-transform hover:scale-110"
        aria-label="Open live chat"
        data-testid="chat-widget-trigger"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+84px)] right-4 lg:bottom-6 lg:right-6 z-40 w-[min(20rem,calc(100vw-2rem))] rounded-xl shadow-2xl border bg-white overflow-hidden flex flex-col" style={{ height: '480px', maxHeight: 'calc(100dvh - 160px)' }} data-testid="chat-widget">
      {/* Header */}
      <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          <span className="font-semibold text-sm">Live Chat</span>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setOpen(false)} className="p-1 hover:bg-blue-700 rounded"><Minimize2 className="h-4 w-4" /></button>
          <button onClick={() => { setOpen(false); setStarted(false); setChatId(null); setMessages([]); }} className="p-1 hover:bg-blue-700 rounded"><X className="h-4 w-4" /></button>
        </div>
      </div>

      {!started ? (
        <div className="flex-1 p-4 space-y-3 overflow-y-auto">
          <p className="text-sm text-gray-600">Chat with our team</p>
          {staff.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-gray-500 font-medium">Available now:</p>
              {staff.map(s => (
                <button
                  key={s.email}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm ${selectedStaff?.email === s.email ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-100'}`}
                  onClick={() => setSelectedStaff(s)}
                >
                  <Circle className="h-2.5 w-2.5 shrink-0" fill={presenceColor[s.presence] || '#9ca3af'} stroke="none" />
                  <span className="truncate">{s.full_name || s.email}</span>
                </button>
              ))}
            </div>
          )}
          {staff.length === 0 && <p className="text-xs text-gray-400">All staff are currently offline. Leave a message and we'll get back to you.</p>}
          <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Your name *" value={name} onChange={e => setName(e.target.value)} />
          <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Email (optional)" value={email} onChange={e => setEmail(e.target.value)} />
          <button
            className="w-full bg-blue-600 text-white rounded py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            onClick={handleStart}
            disabled={!name.trim() || sending}
          >
            Start Chat
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 p-3 overflow-y-auto space-y-2 bg-gray-50">
            {messages.map(m => {
              const isStaff = m.direction === 'outbound';
              return (
                <div key={m.id} className={`flex ${isStaff ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${isStaff ? 'bg-white border' : 'bg-blue-600 text-white'}`}>
                    {isStaff && <p className="text-[10px] font-semibold text-gray-500">{m.sender_name}</p>}
                    <p>{m.message}</p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <div className="p-2 border-t bg-white flex gap-2 shrink-0">
            <input
              className="flex-1 border rounded px-3 py-2 text-sm"
              placeholder="Type a message..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
            />
            <button
              className="bg-blue-600 text-white rounded px-3 hover:bg-blue-700 disabled:opacity-50"
              onClick={handleSend}
              disabled={!input.trim() || sending}
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
